import { ATTR_NAME } from '../../../constants/attr-name'
import { CLASS_NAME } from '../../../constants/class-names'
import {
    ComponentRenderType,
    EventType,
    HtmlAttr,
    KeyType,
    MutationType,
    CarouselType
} from 'constants/enums'
import { Nullable } from 'constants/types'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'

export const WINDOW_RESIZE_TIMEOUT = 300
export const SCROLLING_TIMEOUT = 75

const PEEK_CLONES_PER_SIDE = 3

const { carousel: carouselClass } = CLASS_NAME
const { carousel: carouselAttr } = ATTR_NAME.main

// Abstracted carousel functionality
export class CarouselComponent extends AbstractObserver {
    public static type = ComponentRenderType.CAROUSEL

    public static defaultType = CarouselType.SLIDE
    /* eslint-disable no-magic-numbers */
    public static buttonViewScrollThreshold = 20
    public static extraListPageThreshold = 20
    public static fitItemsBreakpoint = 768 // matches SCSS mq('small')
    public pageIndex = 0
    public lastScrollPosition = 0
    public paginationConfigured = false
    public fullscreenEvent = false

    // Options
    public type: CarouselType
    public slidesToScroll: number
    // Elements & Calculated Values
    public slider: HTMLElement
    public slides: HTMLElement[]
    public totalSlides: number
    public slideItemWidth: number
    // pagination
    public totalPages: number
    public currentPageIndicator: Nullable<HTMLElement>
    public totalPageIndicator: Nullable<HTMLElement>
    public paginationNavigation: Nullable<HTMLElement>
    public paginationNavigationActionItems: HTMLElement[]
    // prev & next
    public prevButton: Nullable<HTMLButtonElement>
    public nextButton: Nullable<HTMLButtonElement>
    public loop: boolean
    public totalRealPages: number
    private peek = false
    private _isLoopTransitioning = false
    private get isLoopTransitioning(): boolean {
        return this._isLoopTransitioning
    }
    private set isLoopTransitioning(value: boolean) {
        this._isLoopTransitioning = value
        if (this.slider) {
            if (value) {
                this.slider.setAttribute('data-carousel-transitioning', '')
            } else {
                this.slider.removeAttribute('data-carousel-transitioning')
            }
        }
    }
    private loopTransitionToken = 0
    private static readonly SMOOTH_SCROLL_TIMEOUT = 500
    /* eslint-enable no-magic-numbers */

    /**
     * Initializes the carousel
     *
     * @param {HTMLElement} el
     */
    constructor(protected sliderContainer: HTMLElement) {
        super(sliderContainer)
        this.activateObserver()
    }

    public render(): void {
        this.type =
            (this.sliderContainer.getAttribute(
                carouselAttr.type
            ) as CarouselType) || CarouselComponent.defaultType

        this.loop = this.sliderContainer.hasAttribute('data-carousel-loop')
        this.peek = this.sliderContainer.hasAttribute(carouselAttr.peek)

        const slider = this.getElement(
            `[${carouselAttr.slideList}]`, // Use backticks and ${} to insert the variable as part of the string
            this.sliderContainer
        )

        if (!slider) {
            return
        }
        this.slider = slider
        this.lastScrollPosition = this.slider.scrollLeft
        this.getSlidesInfo()
        this.fitListItems()
        this.currentPageIndicator = this.getElement(
            `[${carouselAttr.pageNumber}]`,
            this.sliderContainer
        )
        this.totalPageIndicator = this.getElement(
            `[${carouselAttr.pageTotal}]`,
            this.sliderContainer
        )

        this.prevButton = this.getElement<HTMLButtonElement>(
            `[${carouselAttr.prevBtn}]`,
            this.sliderContainer
        )
        this.nextButton = this.getElement<HTMLButtonElement>(
            `[${carouselAttr.nextBtn}]`,
            this.sliderContainer
        )
        this.paginationNavigation =
            this.sliderContainer.querySelector<HTMLElement>(
                `[${carouselAttr.paginationNav}]`
            )
        if (this.paginationNavigation) {
            this.paginationNavigationActionItems = this.getElements(
                `.${carouselClass.paginationListItemButton}`,
                this.paginationNavigation
            )
        }

        // * initialize calcs for first values
        this.totalPages = this.calcTotalPages
        // check if scrolled past page 1
        this.pageIndex = this.currentPageIndex
        this.updatePageTotalUI()
        this.updatePageNumberUI()
        this.configPaginationNavigationItems()
        this.centerListItems()

        // Set up clone-based loop after all elements are ready, before observers attach
        if (this.loop) {
            this.setupLoop()
            this.totalPages = this.totalRealPages
        }

        this.initFocusFollowsSlide()

        // * Attach event handlers to custom buttons
        if (this.prevButton) {
            this.prevButton.addEventListener(
                EventType.CLICK,
                () => this.goPrev(),
                { passive: true }
            )
            this.prevButton.addEventListener(
                EventType.KEYDOWN,
                (e: KeyboardEvent) => {
                    if (e.key == KeyType.ENTER) {
                        e.preventDefault()
                        this.goPrev()
                    }
                }
            )
            // slider starting scrolled later than the first page make prev btn visible
            if (this.pageIndex > 1) {
                this.prevButton.removeAttribute(HtmlAttr.TAB_INDEX)
                this.prevButton.classList.add(carouselClass.btnVisible)
            }
        }
        if (this.nextButton) {
            this.nextButton.addEventListener(
                EventType.CLICK,
                () => this.goNext(),
                { passive: true }
            )
            this.nextButton.addEventListener(
                EventType.KEYDOWN,
                (e: KeyboardEvent) => {
                    if (e.key == KeyType.ENTER) {
                        e.preventDefault()
                        this.goNext()
                    }
                }
            )
            // slider has more than 1 page make next btn visible
            if (this.totalPages > 1) {
                this.nextButton.removeAttribute(HtmlAttr.TAB_INDEX)
                this.nextButton.classList.add(carouselClass.btnVisible)
            }
        }

        // * respond to change events
        // resize will change the responsive slide container width and therefore pages total
        let isResizing: NodeJS.Timeout
        let isCurrentlyResizing = false
        let pageIndexAtResizeStart = 0
        const resizeObserver = new ResizeObserver(
            (entries: ResizeObserverEntry[]) => {
                for (const entry of entries) {
                    if (entry.contentBoxSize) {
                        // Capture pageIndex only on the first event of a resize sequence,
                        // before the scroll handler has a chance to overwrite it with a
                        // wrong value derived from the mid-rotation scroll position.
                        // Also immediately lock the scroll position so the browser's
                        // scroll-snap doesn't flash a neighbouring slide during rotation.
                        if (!isCurrentlyResizing) {
                            pageIndexAtResizeStart = this.pageIndex
                            isCurrentlyResizing = true
                            const domIndexNow = this.loop
                                ? pageIndexAtResizeStart + this.clonesPerSide
                                : pageIndexAtResizeStart
                            this.scrollInstant(
                                this.getSlideScrollLeft(domIndexNow)
                            )
                        }
                        window.clearTimeout(isResizing)

                        // Set a timeout to run after resizing ends
                        isResizing = setTimeout(() => {
                            isCurrentlyResizing = false
                            // Re-snap to the slide that was active before rotation.
                            // Using the captured index avoids the wrong currentPageIndex
                            // that the scroll handler may have computed from the stale
                            // scroll offset during the viewport change.
                            const domIndex = this.loop
                                ? pageIndexAtResizeStart + this.clonesPerSide
                                : pageIndexAtResizeStart
                            this.pageIndex = pageIndexAtResizeStart
                            this.scrollInstant(
                                this.getSlideScrollLeft(domIndex)
                            )
                            // responsive: when resizing has stopped determine if view needs updating
                            this.fitListItems()
                            this.pageIndex = this.currentPageIndex
                            this.updatePageNumberUI()
                            if (this.type == CarouselType.LIST) {
                                this.totalPages = this.calcTotalPages
                                this.updatePageTotalUI()
                                this.configPaginationNavigationItems()
                            }
                            this.handleButtonVisibility()
                            this.centerListItems()
                        }, WINDOW_RESIZE_TIMEOUT)
                    }
                }
            }
        )

        resizeObserver.observe(this.slider)

        const mutationObserver = new MutationObserver(
            (mutationList: MutationRecord[]) => {
                for (const mutation of mutationList) {
                    // the slides within the slider have changed, recalc
                    if (mutation.type === MutationType.CHILD_LIST) {
                        this.getSlidesInfo()
                        this.fitListItems()
                        this.pageIndex = this.currentPageIndex
                        this.updatePageNumberUI()
                        if (this.type == CarouselType.LIST) {
                            this.totalPages = this.calcTotalPages
                            this.updatePageTotalUI()
                            this.configPaginationNavigationItems()
                        }
                        this.handleButtonVisibility()
                    }
                }
            }
        )

        mutationObserver.observe(this.slider, { childList: true })

        // watch if resize was actually fullscreen toggle
        document.addEventListener(EventType.FULLSCREEN_CHANGE, () => {
            this.fullscreenEvent = true
        })

        // user can wheel scroll, touch scroll, keyboard or use custom buttons;
        // keep component data points and view up to date
        // also fires during full screen API change
        let isScrolling: NodeJS.Timeout
        this.slider.addEventListener(
            EventType.SCROLL,
            () => {
                // Clear our timeout throughout the scroll
                window.clearTimeout(isScrolling)

                // Set a timeout to run after scrolling ends
                isScrolling = setTimeout(() => {
                    // responsive: when scrolling has stopped determine if view needs updating
                    if (this.fullscreenEvent) {
                        // if fullscreen event resize recenter the current slide
                        this.goToIndex(this.pageIndex)
                        this.fullscreenEvent = false
                    }
                    // Clone-zone check runs before the isLoopTransitioning guard so
                    // it handles both swipe and button-click wraps. For button-click
                    // wraps isLoopTransitioning stays true until the teleport is done
                    // here, preventing any rapid click from firing goToIndex with the
                    // slider still in the clone zone (which would scroll backward).
                    if (this.loop) {
                        const realFirstScrollLeft = this.getSlideScrollLeft(
                            this.clonesPerSide
                        )
                        const realLastScrollLeft = this.getSlideScrollLeft(
                            this.totalRealPages + this.clonesPerSide - 1
                        )
                        const firstRealDomIndex = this.clonesPerSide
                        const inLeadingZone =
                            this.slider.scrollLeft < realFirstScrollLeft - 1
                        const inTrailingZone =
                            this.slider.scrollLeft > realLastScrollLeft + 1
                        if (inLeadingZone || inTrailingZone) {
                            // Map the rounded current DOM index to the matching
                            // real slide so a 2-slide overshoot (fast swipe)
                            // lands on the user's intended slide, not real0.
                            const rawDomIndex = Math.round(
                                this.slider.scrollLeft / this.pageWidth
                            )
                            const targetReal =
                                (((rawDomIndex - firstRealDomIndex) %
                                    this.totalRealPages) +
                                    this.totalRealPages) %
                                this.totalRealPages
                            // Defer the scrollLeft jump one frame so iOS Safari
                            // commits the snap position to its compositor layer
                            // before we reposition. Without rAF the layer and
                            // scroll position desync for one frame, visible as
                            // a brief flicker on iPad at the loop wrap moment.
                            // isLoopTransitioning stays true across the gap so
                            // any rapid follow-up input defers until teleport.
                            this.isLoopTransitioning = true
                            requestAnimationFrame(() => {
                                this.scrollInstant(
                                    this.getSlideScrollLeft(
                                        firstRealDomIndex + targetReal
                                    )
                                )
                                this.pageIndex = targetReal
                                this.isLoopTransitioning = false
                                this.updatePageNumberUI()
                                this.handleButtonVisibility()
                            })
                            return
                        }
                    }
                    if (this.isLoopTransitioning) {
                        return
                    }
                    this.pageIndex = this.currentPageIndex
                    this.updatePageNumberUI()
                    this.handleButtonVisibility()
                    this.handlePaginationNavigationActiveView()
                }, SCROLLING_TIMEOUT)
            },
            { passive: true }
        )

        let interactionStartPageIndex: number | undefined
        let interactionStartSlide: HTMLElement | undefined
        let suppressNextClick = false
        const clearInteractionStart = (): void => {
            interactionStartPageIndex = undefined
            interactionStartSlide = undefined
        }
        this.slider.addEventListener(
            'mousedown',
            (e: MouseEvent) => {
                interactionStartSlide = this.getSlideFromEventTarget(e.target)
                interactionStartPageIndex = interactionStartSlide
                    ? this.pageIndex
                    : undefined
            },
            { passive: true, capture: true }
        )
        this.slider.addEventListener(
            'mouseup',
            () => {
                window.setTimeout(clearInteractionStart, 0)
            },
            { passive: true, capture: true }
        )
        this.slider.addEventListener(
            EventType.CLICK,
            (e: MouseEvent) => {
                if (suppressNextClick) {
                    suppressNextClick = false
                    e.preventDefault()
                    e.stopPropagation()
                    return
                }

                const slide = this.getSlideFromEventTarget(e.target)
                const sourcePageIndex =
                    slide && slide === interactionStartSlide
                        ? interactionStartPageIndex
                        : undefined
                const targetIndex =
                    slide &&
                    this.getPeekSlideActivationIndex(slide, sourcePageIndex)
                clearInteractionStart()

                if (targetIndex === undefined) {
                    return
                }

                e.preventDefault()
                e.stopPropagation()
                this.activatePeekSlide(targetIndex, sourcePageIndex)
            },
            { capture: true }
        )

        // On iOS Safari, the native gesture recogniser treats <img> elements as
        // potential image-interaction targets (drag, peek) at the compositor level,
        // which prevents the overflow-x scroll from starting when a swipe begins on
        // the image area. pointer-events:none on <img>/<picture> fixes the JS event
        // target but does not reach the compositor.
        //
        // Two fallbacks handle the image area:
        //   1. Swipe fallback  — if no scroll event fired during the touch, a
        //      horizontal swipe calls goNext/goPrev directly.
        //   2. Tap fallback    — a short tap calls link.click(), working around
        //      iOS not synthesising click events on <a> inside overflow-x containers
        //      when touch-action:pan-x is in effect.
        //
        // capture:true ensures both handlers fire before any bubble-phase handler
        // on a child element (e.g. an analytics library) can call stopPropagation.
        const TAP_MAX_PX = 15
        const TAP_MAX_MS = 600
        const SWIPE_MIN_PX = 30
        let tapX = 0
        let tapY = 0
        let tapT = 0
        let scrolledDuringTouch = false
        this.slider.addEventListener(
            EventType.TOUCH_START,
            (e: TouchEvent) => {
                if (this.isLoopTransitioning) {
                    return
                }
                tapX = e.touches[0].clientX
                tapY = e.touches[0].clientY
                tapT = Date.now()
                interactionStartPageIndex = this.pageIndex
                interactionStartSlide = this.getSlideFromEventTarget(e.target)
                scrolledDuringTouch = false
            },
            { passive: true }
        )
        this.slider.addEventListener(
            EventType.SCROLL,
            () => {
                scrolledDuringTouch = true
            },
            { passive: true, capture: true }
        )
        // capture:true fires before any bubble-phase handler lower in the tree
        // (e.g. analytics libraries that call stopPropagation on the article card).
        this.slider.addEventListener(
            EventType.TOUCH_END,
            (e: TouchEvent) => {
                const endX = e.changedTouches[0].clientX
                const endY = e.changedTouches[0].clientY
                const deltaX = endX - tapX
                const deltaY = endY - tapY
                const duration = Date.now() - tapT

                // Swipe fallback: horizontal swipe that did not trigger native scroll
                // (iOS image area). Only act if the gesture is clearly horizontal.
                if (
                    !scrolledDuringTouch &&
                    Math.abs(deltaX) >= SWIPE_MIN_PX &&
                    Math.abs(deltaX) > Math.abs(deltaY)
                ) {
                    clearInteractionStart()
                    if (deltaX < 0) {
                        this.goNext()
                    } else {
                        this.goPrev()
                    }
                    return
                }

                // Tap fallback (loop carousel only): short tap with minimal movement.
                // On the loop carousel, iOS targets <article data-evt="click">
                // (the FEM analytics element) as the touch target instead of the
                // <a> inside it, so the natural tap-to-click conversion does not
                // navigate. elementFromPoint uses the recorded tap coordinates:
                // pointer-events:none on <picture>/<img> causes it to return
                // <a class="article-card__image-link"> directly.
                if (
                    this.loop &&
                    duration <= TAP_MAX_MS &&
                    Math.abs(deltaX) < TAP_MAX_PX &&
                    Math.abs(deltaY) < TAP_MAX_PX
                ) {
                    // elementsFromPoint returns all elements at the tap position
                    // regardless of pointer-events, so the first <a href> in the
                    // list is the link directly under the finger. This avoids
                    // relying on e.target (which iOS routes to <article> when FEM
                    // has a click listener there) or any card-specific class names.
                    const elements = document.elementsFromPoint(
                        tapX,
                        tapY
                    ) as Element[]
                    const tappedSlide = this.getSlideFromElements(elements)
                    const targetIndex =
                        tappedSlide &&
                        this.getPeekSlideActivationIndex(
                            tappedSlide,
                            interactionStartPageIndex
                        )
                    clearInteractionStart()

                    if (targetIndex !== undefined) {
                        suppressNextClick = true
                        this.activatePeekSlide(
                            targetIndex,
                            interactionStartPageIndex
                        )
                        return
                    }

                    const linkSlide = this.isPeekRendered()
                        ? tappedSlide
                        : this.getActiveSlide()
                    const link = this.getLinkAtPoint(
                        linkSlide,
                        elements,
                        tapX,
                        tapY
                    )
                    if (!link?.href) {
                        return
                    }
                    link.click()
                }
            },
            { capture: true }
        )

        // Shared hook: autoplay assigns pausePermanently here so the wheel
        // handler (set up first) can stop autoplay on a desktop trackpad swipe.
        let onWheelSwipe: (() => void) | undefined

        // Desktop trackpad / mouse-wheel swipe support (loop carousel only).
        // The loop carousel uses cloned slides, so native overflow-x scroll
        // does not work correctly — native scroll can land in the clone zone.
        // CSS scroll-behavior:smooth + scroll-snap-type:x mandatory also conflict
        // with native horizontal wheel input on Chrome. We intercept clearly-
        // horizontal wheel events and drive navigation via goNext/goPrev instead.
        if (this.loop) {
            let wheelCooldown = false
            let wheelAccumX = 0
            let wheelResetTimer: ReturnType<typeof setTimeout>
            const WHEEL_THRESHOLD = 30
            const WHEEL_GESTURE_RESET_MS = 150
            const WHEEL_COOLDOWN_MS = 800
            this.slider.addEventListener(
                EventType.WHEEL,
                (e: WheelEvent) => {
                    // Only skip pure vertical scrolls so the page can still scroll
                    // vertically when hovering over the carousel.
                    if (e.deltaX === 0) {
                        return
                    }
                    e.preventDefault()
                    if (wheelCooldown) {
                        return
                    }
                    wheelAccumX += e.deltaX
                    window.clearTimeout(wheelResetTimer)
                    // Reset accumulator if the gesture pauses (new swipe gesture starting).
                    wheelResetTimer = setTimeout(() => {
                        wheelAccumX = 0
                    }, WHEEL_GESTURE_RESET_MS)
                    if (Math.abs(wheelAccumX) >= WHEEL_THRESHOLD) {
                        onWheelSwipe?.()
                        if (wheelAccumX > 0) {
                            this.goNext()
                        } else {
                            this.goPrev()
                        }
                        wheelAccumX = 0
                        // Cooldown prevents rapid-firing during the momentum tail of a swipe.
                        wheelCooldown = true
                        setTimeout(() => {
                            wheelCooldown = false
                        }, WHEEL_COOLDOWN_MS)
                    }
                },
                { passive: false }
            )
        } // end if (this.loop)

        // highjack natural arrow to use component function
        this.slider.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
            switch (e.key) {
                case KeyType.RIGHT:
                case KeyType.RIGHT_IE:
                    e.preventDefault()
                    this.goNext()
                    break
                case KeyType.LEFT:
                case KeyType.LEFT_IE:
                    e.preventDefault()
                    this.goPrev()
                    break
                default:
                    break
            }
        })

        // Autoplay: read interval from data attribute (loop carousel only).
        // Attribute is present only when auto-play is enabled in admin config.
        // Default interval is 5 s when the attribute value is absent or invalid.
        const autoplayAttr =
            this.sliderContainer.getAttribute(carouselAttr.autoplay) ??
            undefined
        if (this.loop && autoplayAttr !== undefined) {
            const MS_PER_SECOND = 1000
            const DEFAULT_AUTOPLAY_SECONDS = 5
            const parsedSeconds = parseInt(autoplayAttr, 10)
            const interval =
                (isNaN(parsedSeconds) || parsedSeconds <= 0
                    ? DEFAULT_AUTOPLAY_SECONDS
                    : parsedSeconds) * MS_PER_SECOND

            let userPaused = false
            let autoplayTimer: ReturnType<typeof setInterval>

            const startAutoplay = (): void => {
                clearInterval(autoplayTimer)
                autoplayTimer = setInterval(() => {
                    this.goNext()
                }, interval)
            }
            const stopAutoplay = (): void => {
                clearInterval(autoplayTimer)
            }
            // Called on any deliberate user navigation — stops autoplay for the session.
            // Tab visibility is the only event that can restart it after this.
            const pausePermanently = (): void => {
                userPaused = true
                stopAutoplay()
            }
            onWheelSwipe = pausePermanently

            startAutoplay()

            // Desktop hover: transient pause — resumes on mouse leave unless the user
            // already took permanent control via navigation.
            this.sliderContainer.addEventListener(
                EventType.MOUSE_ENTER,
                stopAutoplay,
                { passive: true }
            )
            this.sliderContainer.addEventListener(
                EventType.MOUSE_LEAVE,
                () => {
                    if (!userPaused) {
                        startAutoplay()
                    }
                },
                { passive: true }
            )

            // Keyboard focus inside the carousel: permanent pause so autoplay
            // doesn't yank inert onto the focused slide mid-tab.
            this.sliderContainer.addEventListener(
                EventType.FOCUS,
                pausePermanently,
                { passive: true, capture: true }
            )

            // Touch (swipe or tap): permanent pause — user is actively interacting.
            this.sliderContainer.addEventListener(
                EventType.TOUCH_START,
                pausePermanently,
                { passive: true }
            )

            // Manual navigation via prev/next buttons or pagination dots: permanent pause.
            this.sliderContainer.addEventListener(
                EventType.CLICK,
                (e: Event) => {
                    const target = e.target as Element
                    if (
                        target.closest(`[${carouselAttr.prevBtn}]`) ||
                        target.closest(`[${carouselAttr.nextBtn}]`) ||
                        target.closest(`[${carouselAttr.paginationNavToIndex}]`)
                    ) {
                        pausePermanently()
                    }
                },
                { passive: true }
            )

            // Tab visibility: pause when the tab is hidden, resume when it returns.
            // This is the only path that overrides a permanent user pause.
            document.addEventListener(EventType.VISIBILITY_CHANGE, () => {
                if (document.hidden) {
                    stopAutoplay()
                } else {
                    userPaused = false
                    startAutoplay()
                }
            })
        }
    }

    private getSlideFromEventTarget(
        target: EventTarget | null
    ): HTMLElement | undefined {
        if (!(target instanceof Element)) {
            return undefined
        }
        const slide = target.closest<HTMLElement>(`[${carouselAttr.slideItem}]`)
        if (!slide || !this.slider.contains(slide)) {
            return undefined
        }
        return slide
    }

    private getSlideFromElements(elements: Element[]): HTMLElement | undefined {
        return elements
            .map((el) => el.closest<HTMLElement>(`[${carouselAttr.slideItem}]`))
            .find((slide): slide is HTMLElement =>
                Boolean(slide && this.slider.contains(slide))
            )
    }

    private getPeekSlideActivationIndex(
        slide: HTMLElement,
        sourcePageIndex: number | undefined
    ): number | undefined {
        if (!this.isPeekRendered() || this.type !== CarouselType.SLIDE) {
            return undefined
        }
        const domIndex = this.slides.indexOf(slide)
        if (domIndex < 0 || (this.loop && this.totalRealPages < 1)) {
            return undefined
        }
        const targetIndex = this.loop
            ? (((domIndex - this.clonesPerSide) % this.totalRealPages) +
                  this.totalRealPages) %
              this.totalRealPages
            : domIndex
        const activeAtInteractionStart = sourcePageIndex ?? this.pageIndex

        return targetIndex === activeAtInteractionStart
            ? undefined
            : targetIndex
    }

    private activatePeekSlide(
        targetIndex: number,
        sourcePageIndex: number | undefined
    ): void {
        const activeAtInteractionStart = sourcePageIndex ?? this.pageIndex
        if (this.loop && this.totalRealPages > 1) {
            const nextIndex =
                (activeAtInteractionStart + 1) % this.totalRealPages
            const prevIndex =
                (activeAtInteractionStart - 1 + this.totalRealPages) %
                this.totalRealPages

            if (targetIndex === nextIndex) {
                this.goNext()
                return
            }
            if (targetIndex === prevIndex) {
                this.goPrev()
                return
            }
        }
        this.goToIndex(targetIndex)
    }

    private isPeekRendered(): boolean {
        if (!this.peek || !this.slider) {
            return false
        }
        const paddingInlineStart = parseFloat(
            getComputedStyle(this.slider).getPropertyValue(
                'padding-inline-start'
            )
        )
        return !Number.isNaN(paddingInlineStart) && paddingInlineStart > 0
    }

    private getActiveSlide(): HTMLElement | undefined {
        const domIndex = this.loop
            ? this.pageIndex + this.clonesPerSide
            : this.pageIndex
        return this.slides?.[domIndex]
    }

    private getLinkAtPoint(
        slide: HTMLElement | undefined,
        elements: Element[],
        x: number,
        y: number
    ): HTMLAnchorElement | undefined {
        if (!slide) {
            return undefined
        }
        const linkFromStack = elements.find(
            (el): el is HTMLAnchorElement =>
                el instanceof HTMLAnchorElement &&
                !!el.href &&
                slide.contains(el)
        )
        if (linkFromStack) {
            return linkFromStack
        }
        return [...slide.querySelectorAll<HTMLAnchorElement>('a[href]')].find(
            (link) => {
                const rect = link.getBoundingClientRect()
                return (
                    x >= rect.left &&
                    x <= rect.right &&
                    y >= rect.top &&
                    y <= rect.bottom
                )
            }
        )
    }

    private get itemStep(): number {
        const first = this.slides?.[0]
        if (!first) return this.slider?.clientWidth ?? 0
        const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0
        return first.getBoundingClientRect().width + gap
    }

    /**
     * get the most up to date slider width for page width calcs
     * (changes on resize)
     * @return {number}
     */
    get pageWidth(): number {
        if (!this.slider) {
            return 0
        }
        if (this.peek) {
            return this.itemStep
        }
        return this.slider.clientWidth
    }

    /**
     * calc how many horizontal view "pages" are available to scroll in the element
     * @return {number}
     */
    get calcTotalPages(): number {
        if (!this.slider) {
            return 0
        }

        switch (this.type) {
            case CarouselType.LIST: {
                // if all elements are in view on current screen size there is only 1 page
                if (
                    this.slider.scrollWidth - this.slider.clientWidth <=
                    CarouselComponent.buttonViewScrollThreshold
                ) {
                    return 1
                }
                // do the pages fit within the scroll width available
                const pageRemainder = this.slider.scrollWidth % this.pageWidth
                let pagesPerPageWidth = Math.floor(
                    this.slider.scrollWidth / this.pageWidth
                )
                // if the remainder was large enough add another page
                // to be able to scroll to it
                if (pageRemainder >= CarouselComponent.extraListPageThreshold) {
                    pagesPerPageWidth += 1
                }
                return pagesPerPageWidth
            }
            case CarouselType.SLIDE:
            default:
                return this.totalSlides
        }
    }

    /**
     * calc which page the horizontal scroll is sitting at
     * manual scroll can end up half way between 2 pages
     * @return {number}
     */
    get currentPageIndex(): number {
        if (!this.slider) {
            return 0
        }
        // Element scroll left 0 = first page
        if (this.slider.scrollLeft == 0) {
            return 0
        }
        const pageScroll = parseFloat(
            (this.slider.scrollLeft / this.pageWidth).toFixed(2)
        )

        switch (this.type) {
            case CarouselType.LIST: {
                const listPageThreshold = 0.2

                if (pageScroll % 1 > listPageThreshold) {
                    return Math.ceil(pageScroll)
                } else {
                    return Math.round(pageScroll)
                }
            }
            case CarouselType.SLIDE:
            default: {
                const rawIndex = Math.round(pageScroll)
                if (this.loop) {
                    // Offset by the number of prepended clones so real[0] maps to index 0
                    return Math.max(
                        0,
                        Math.min(
                            rawIndex - this.clonesPerSide,
                            this.totalRealPages - 1
                        )
                    )
                }
                return rawIndex
            }
        }
    }

    /**
     * calc how much to shift the scroll
     * to an index to shift to certain page location/distance
     * @return {number} shift total
     */
    public indexScrollingDistance(index: number): number {
        if (!this.slider) {
            return 0
        }
        const indexDistance = this.pageWidth * index
        const totalDistance = this.slider.scrollWidth
        const finalDistance =
            totalDistance < indexDistance ? totalDistance : indexDistance
        return finalDistance
    }

    /**
    /**
     * when navigating keep track of the current page
     * update last scroll position tracker
     * update pagination in UI
     * @param {number}
     */
    public updatePageNumberUI(): void {
        this.lastScrollPosition = this.slider ? this.slider.scrollLeft : 0
        const currentPage = this.pageIndex + 1
        if (this.currentPageIndicator) {
            this.currentPageIndicator.textContent = currentPage.toString()
        }
    }

    /**
     * determine if a responsive reflow has changed the total pages
     * update pagination in UI
     */
    public updatePageTotalUI(): void {
        if (
            this.totalPageIndicator &&
            parseInt(this.totalPageIndicator.textContent as string) !=
                this.totalPages
        ) {
            this.totalPageIndicator.textContent = this.totalPages.toString()
        }
    }

    public getSlidesInfo(): void {
        this.slides = this.getElements(
            `[${carouselAttr.slideItem}]`, // Use backticks and ${} to insert the variable as part of the string
            this.slider
        )
        this.totalSlides = this.slides.length
        this.slideItemWidth =
            this.slides && this.slides[0] ? this.slides[0].clientWidth : 0
    }

    /**
     * For carousels with data-carousel-fit-items="min,max":
     * On small screens, adjust item widths so an exact integer number (min–max)
     * fills the viewport with no partial items. Only runs when the attribute
     * is present and content overflows on screens below the fit breakpoint.
     */
    public fitListItems(): void {
        const fitAttr = this.sliderContainer.getAttribute(carouselAttr.fitItems)
        if (!fitAttr || !this.slides.length) {
            return
        }
        const containerWidth = this.pageWidth
        if (containerWidth <= 0) {
            return
        }

        // Reset to natural sizes so we can measure accurately
        this.slides.forEach((slide) => {
            slide.style.width = ''
            slide.style.minWidth = ''
        })
        this.getSlidesInfo()

        // Only apply on small screens when content overflows
        if (
            window.innerWidth >= CarouselComponent.fitItemsBreakpoint ||
            this.slider.scrollWidth <= containerWidth
        ) {
            return
        }

        const [minItems, maxItems] = fitAttr.split(',').map(Number)

        // Measure gap from actual DOM positions
        const gap =
            this.slides.length > 1
                ? this.slides[1].offsetLeft -
                  this.slides[0].offsetLeft -
                  this.slides[0].offsetWidth
                : 0
        const naturalWidth = this.slides[0].offsetWidth

        // How many items fit at their natural width?
        let itemsPerView = Math.floor(
            (containerWidth + gap) / (naturalWidth + gap)
        )
        itemsPerView = Math.max(minItems, Math.min(maxItems, itemsPerView))

        // Exact width: n items + (n-1) gaps = containerWidth
        const exactWidth =
            (containerWidth - (itemsPerView - 1) * gap) / itemsPerView

        this.slides.forEach((slide) => {
            slide.style.width = `${exactWidth}px`
            slide.style.minWidth = `${exactWidth}px`
        })
        this.getSlidesInfo()
    }

    /**
     * For LIST type carousels, center items via padding when they
     * fit within the container. This replicates 'justify-content: safe center'
     * Centers items via padding when they fit within the container.
     */
    public centerListItems(): void {
        if (this.type !== CarouselType.LIST || !this.slides.length) {
            return
        }

        const containerWidth = this.slider.clientWidth
        const lastSlide = this.slides[this.slides.length - 1]
        const firstSlide = this.slides[0]
        const contentWidth =
            lastSlide.offsetLeft + lastSlide.offsetWidth - firstSlide.offsetLeft

        if (contentWidth < containerWidth) {
            const padding = Math.round((containerWidth - contentWidth) / 2)
            const currentPadding = parseFloat(
                this.slider.style.paddingInlineStart || '0'
            )
            if (Math.abs(padding - currentPadding) > 1) {
                this.slider.style.paddingInlineStart = `${padding}px`
            }
        } else {
            this.slider.style.paddingInlineStart = '0'
        }
    }

    /**
     * determine if a responsive reflow has changed the total pages
     * update pagination in UI
     */
    public getPaginationNavItemIndex(navBtn: Element): number {
        if (!navBtn) {
            return 0
        }
        return parseInt(
            navBtn.getAttribute(carouselAttr.paginationNavToIndex) || '0'
        )
    }

    /**
     * for each navigation item button attach click event
     * to navigate to given index
     */
    public attachPaginationNavItemEvents(): void {
        if (
            this.paginationNavigationActionItems &&
            this.paginationNavigationActionItems.length > 0
        ) {
            this.paginationNavigationActionItems.forEach((navBtn) => {
                if (navBtn) {
                    const index = this.getPaginationNavItemIndex(navBtn)
                    navBtn.addEventListener(
                        EventType.CLICK,
                        () => this.goToIndex(index),
                        { passive: true }
                    )
                    navBtn.addEventListener(
                        EventType.KEYDOWN,
                        (e: KeyboardEvent) => {
                            if (e.key == KeyType.ENTER) {
                                e.preventDefault()
                                this.goToIndex(index)
                            }
                        }
                    )
                }
            })
        }
    }

    /**
     * Check that the pagination items were built/exist before removing hidden attribute
     * call fn to attach events to the buttons
     * Set flag that this has complete (at least once)
     */
    public showPaginationNavigation(): void {
        if (!this.paginationNavigation) {
            return
        }
        if (
            this.paginationNavigation.hidden &&
            this.paginationNavigationActionItems.length > 0
        ) {
            this.paginationNavigation.hidden = false
        }
        this.attachPaginationNavItemEvents()
        this.handlePaginationNavigationActiveView()
        this.paginationConfigured = true
    }

    /**
     * Run the configuration logic for Carousel Pagination depending on CarouselType
     * TYPE.SLIDE can easily build 1:1 list of navigation items, attach events
     * TYPE.LIST navigation items need to be built with params/responsive to screen
     */
    public configPaginationNavigationItems(): void {
        if (!this.paginationNavigation) {
            return
        }

        if (this.type == CarouselType.SLIDE && this.paginationConfigured) {
            // only TYPE.LIST would need to rebuild pagination
            // for responding to screen changes
            // Exit for type slide with configured flag true
            return
        }

        switch (this.type) {
            case CarouselType.LIST:
                this.buildPaginationNavigationItems()
                this.paginationNavigationActionItems = this.getElements(
                    `.${carouselClass.paginationListItemButton}`,
                    this.paginationNavigation
                )
                this.showPaginationNavigation()
                break
            case CarouselType.SLIDE:
            default:
                this.showPaginationNavigation()
                break
        }
    }

    /**
     * Place an active class on the current active pagination item
     * that matches the carousel's current page
     */
    public handlePaginationNavigationActiveView(): void {
        let currentActiveNavItemIndex = 0
        if (
            !this.paginationNavigation ||
            !this.paginationNavigationActionItems
        ) {
            return
        }
        // is it the first time?
        // initialize the current active item
        if (!this.paginationConfigured) {
            const currentPageNavItem = this.getElement(
                `[${carouselAttr.paginationNavToIndex}="${this.pageIndex}"]`,
                this.paginationNavigation
            )
            if (currentPageNavItem) {
                currentPageNavItem.classList.add(
                    carouselClass.paginationListItemActive
                )
            }
            return
        }
        // second time
        // What is the current active pagination item
        const currentActiveNavItem = this.getElement(
            `.${carouselClass.paginationListItemActive}`,
            this.paginationNavigation
        )
        if (currentActiveNavItem) {
            currentActiveNavItemIndex =
                this.getPaginationNavItemIndex(currentActiveNavItem)
        }
        // if the current active pagination item and the current page are the same
        // exit and make no changes
        if (
            currentActiveNavItemIndex == this.pageIndex &&
            currentActiveNavItemIndex < this.totalPages
        ) {
            return
        }
        // not the same -- find the pagination item that does match the current page
        // -1 for 0 index
        const currentPageNavItem = this.getElement(
            `[${carouselAttr.paginationNavToIndex}="${this.pageIndex}"]`,
            this.paginationNavigation
        )

        // swap the classes from the old active to new active item
        if (currentActiveNavItem) {
            currentActiveNavItem.classList.remove(
                carouselClass.paginationListItemActive
            )
        }
        if (currentPageNavItem) {
            currentPageNavItem.classList.add(
                carouselClass.paginationListItemActive
            )
        }
    }

    /**
     * For single-slide carousels, advance the carousel to whichever slide
     * receives keyboard focus. This keeps all slide content in the natural tab
     * order while ensuring the focused slide is always visible.
     * Called once during render(); listeners persist for the lifetime of the component.
     */
    private initFocusFollowsSlide(): void {
        if (this.type !== CarouselType.SLIDE) return
        this.slides.forEach((slide, domIndex) => {
            slide.addEventListener(
                EventType.FOCUS,
                () => {
                    // Skip clone slides that exist only for the loop animation.
                    // Loop layout: DOM 0…clonesPerSide-1 = leading clones,
                    // clonesPerSide…N+clonesPerSide-1 = real slides,
                    // remaining = trailing clones.
                    if (
                        this.loop &&
                        (domIndex < this.clonesPerSide ||
                            domIndex >=
                                this.totalRealPages + this.clonesPerSide)
                    ) {
                        return
                    }
                    const targetPageIndex = this.loop
                        ? domIndex - this.clonesPerSide
                        : domIndex
                    if (targetPageIndex !== this.pageIndex) {
                        this.goToIndex(targetPageIndex)
                    }
                },
                { capture: true }
            )
        })
    }

    /**
     * determine which direction buttons should be visible
     * apply tabindex -1 to hidden buttons for keyboard navigation
     */
    public handleButtonVisibility(): void {
        // In loop mode, always show both buttons
        if (this.loop) {
            if (this.prevButton) {
                this.prevButton.removeAttribute(HtmlAttr.TAB_INDEX)
                this.prevButton.classList.add(carouselClass.btnVisible)
            }
            if (this.nextButton) {
                this.nextButton.removeAttribute(HtmlAttr.TAB_INDEX)
                this.nextButton.classList.add(carouselClass.btnVisible)
            }
            return
        }

        // Check previous button
        if (this.prevButton) {
            // is the scrolled amount less than threshold?
            // then there is no previous to go to
            if (
                this.lastScrollPosition <=
                CarouselComponent.buttonViewScrollThreshold
            ) {
                this.prevButton.tabIndex = -1
                this.prevButton.classList.remove(carouselClass.btnVisible)
            } else {
                this.prevButton.removeAttribute(HtmlAttr.TAB_INDEX)
                this.prevButton.classList.add(carouselClass.btnVisible)
            }
        }

        // Check next button
        if (this.nextButton && this.slider) {
            // is the scrolled amount more than or equal to the elements available scroll width?
            // then there is no next to go to
            if (
                this.pageIndex >= this.totalPages ||
                this.slider.scrollWidth -
                    this.slider.clientWidth -
                    this.lastScrollPosition <=
                    CarouselComponent.buttonViewScrollThreshold
            ) {
                this.nextButton.tabIndex = -1
                this.nextButton.classList.remove(carouselClass.btnVisible)
            } else {
                this.nextButton.removeAttribute(HtmlAttr.TAB_INDEX)
                this.nextButton.classList.add(carouselClass.btnVisible)
            }
        }
    }

    /**
     * scroll the previous slide or slides in view
     * scroll event will be triggered and reconsider view values
     */
    public goPrev(): void {
        if (!this.slider) {
            return
        }
        if (this.loop) {
            // Block any navigation while a loop animation or teleport is in progress.
            // Without this, a rapid click after afterSmoothScroll updates pageIndex
            // (but before the scroll handler teleports from the clone position) would
            // call goToIndex from the clone position and scroll backward.
            if (this.isLoopTransitioning) {
                return
            }
            if (this.pageIndex === 0) {
                // Smooth scroll to the clone-last slot, then instantly teleport to real last.
                // In peek mode, clone-last is the nearest leading clone.
                this.isLoopTransitioning = true
                const token = ++this.loopTransitionToken
                this.slider.scroll({
                    left: this.getSlideScrollLeft(this.clonesPerSide - 1),
                    behavior: 'smooth' as ScrollBehavior
                })
                this.afterSmoothScroll(() => {
                    if (token !== this.loopTransitionToken) {
                        return
                    }
                    this.pageIndex = this.totalRealPages - 1
                    this.updatePageNumberUI()
                    this.handleButtonVisibility()
                    // isLoopTransitioning is cleared by the scroll handler's
                    // clone-zone check after the teleport, not here.
                })
                return
            }
            this.goToIndex(this.pageIndex - 1)
            return
        }

        // LIST type: scroll back by one viewport width from current position
        // scroll-snap aligns to the nearest item boundary
        if (this.type === CarouselType.LIST) {
            this.slider.scrollLeft = Math.max(
                0,
                this.slider.scrollLeft - this.pageWidth
            )
            this.pageIndex = this.currentPageIndex
            this.updatePageNumberUI()
            this.handleButtonVisibility()
            this.handlePaginationNavigationActiveView()
            return
        }

        const prevIndex = this.pageIndex > 0 ? this.pageIndex - 1 : 0
        this.goToIndex(prevIndex)
    }

    /**
     * scroll the next slide or slides in views
     * scroll event will be triggered and reconsider view values
     */
    public goNext(): void {
        if (!this.slider) {
            return
        }
        if (this.loop) {
            // Block any navigation while a loop animation or teleport is in progress.
            // Same rationale as goPrev: after afterSmoothScroll updates pageIndex the
            // slider is still at the clone position until the scroll handler teleports.
            if (this.isLoopTransitioning) {
                return
            }
            if (this.pageIndex >= this.totalRealPages - 1) {
                // Smooth scroll to the clone-first slot, then teleport to real first.
                // clone-first sits just after the last real slide.
                this.isLoopTransitioning = true
                const token = ++this.loopTransitionToken
                this.slider.scroll({
                    left: this.getSlideScrollLeft(
                        this.totalRealPages + this.clonesPerSide
                    ),
                    behavior: 'smooth' as ScrollBehavior
                })
                this.afterSmoothScroll(() => {
                    if (token !== this.loopTransitionToken) {
                        return
                    }
                    this.pageIndex = 0
                    this.updatePageNumberUI()
                    this.handleButtonVisibility()
                    // isLoopTransitioning is cleared by the scroll handler's
                    // clone-zone check after the teleport, not here.
                })
                return
            }
            this.goToIndex(this.pageIndex + 1)
            return
        }

        // LIST type: scroll forward by one viewport width from current position
        // scroll-snap aligns to the nearest item boundary
        if (this.type === CarouselType.LIST) {
            const maxScroll = this.slider.scrollWidth - this.pageWidth
            this.slider.scrollLeft = Math.min(
                maxScroll,
                this.slider.scrollLeft + this.pageWidth
            )
            this.pageIndex = this.currentPageIndex
            this.updatePageNumberUI()
            this.handleButtonVisibility()
            this.handlePaginationNavigationActiveView()
            return
        }

        const nextIndex =
            this.pageIndex < this.totalPages - 1
                ? this.pageIndex + 1
                : this.totalPages - 1
        this.goToIndex(nextIndex)
    }

    /**
     * scroll the given slide index
     * scroll event will be triggered and reconsider view values
     */
    public goToIndex(index: number): void {
        if (!this.slider) {
            return
        }
        const domIndex = this.loop ? index + this.clonesPerSide : index
        if (this.loop) {
            // Prevent the scroll event handler from overriding pageIndex mid-animation.
            // Without this, rapid clicks read an intermediate scrollLeft and reset
            // pageIndex to the wrong value, causing the slider to jump backward.
            this.isLoopTransitioning = true
            // Token ensures only the latest animation clears the transitioning flag.
            // A rapid second click increments the token; the first click's callback
            // then sees a stale token and won't prematurely clear the flag.
            const token = ++this.loopTransitionToken
            this.slider.scroll({
                left: this.indexScrollingDistance(domIndex),
                behavior: 'smooth' as ScrollBehavior
            })
            this.afterSmoothScroll(() => {
                if (token === this.loopTransitionToken) {
                    requestAnimationFrame(() => {
                        this.isLoopTransitioning = false
                    })
                }
            })
        } else {
            this.slider.scroll({
                left: this.indexScrollingDistance(domIndex),
                behavior: 'smooth' as ScrollBehavior
            })
        }
        this.pageIndex = index
        this.updatePageNumberUI()
        this.handleButtonVisibility()
    }

    /**
     * Jump to the given slide index instantly (no smooth scroll animation).
     * Used for loop wrap-around to avoid scrolling through all slides.
     */
    public goToIndexInstant(index: number): void {
        if (!this.slider) {
            return
        }
        const domIndex = this.loop ? index + this.clonesPerSide : index
        this.scrollInstant(this.indexScrollingDistance(domIndex))
        this.pageIndex = index
        this.updatePageNumberUI()
        this.handleButtonVisibility()
    }

    /**
     * Set up clone-based infinite loop, then reposition to the first real slide.
     * Peek mode uses extra clones on both sides so adjacent slots stay filled.
     */
    private setupLoop(): void {
        const serverCloneLast = this.slider.querySelector<HTMLElement>(
            '[data-carousel-server-clone]'
        )
        const realSlides = this.getElements(
            `[${carouselAttr.slideItem}]:not([data-carousel-server-clone])`,
            this.slider
        )
        this.totalRealPages = realSlides.length

        if (this.totalRealPages < 2) {
            this.loop = false
            this.slider.setAttribute('data-carousel-ready', 'true')
            return
        }

        // Extra leading clones keep the left peek filled during fast swipes.
        const leadingExtras = this.clonesPerSide - 1

        // Prepend extra clones outermost-first so final DOM order is
        // [...extraLeading, cloneLast, real0, ...]
        for (let k = leadingExtras; k >= 1; k--) {
            const idx =
                (this.totalRealPages - k - 1 + this.totalRealPages) %
                this.totalRealPages
            const clone = realSlides[idx].cloneNode(true) as HTMLElement
            this.eagerLoadCloneImages(clone)
            this.disableCloneFocus(clone)
            this.slider.insertBefore(clone, serverCloneLast ?? realSlides[0])
        }

        const cloneLast =
            serverCloneLast ??
            (realSlides[this.totalRealPages - 1].cloneNode(true) as HTMLElement)
        if (!serverCloneLast) {
            this.eagerLoadCloneImages(cloneLast)
            this.slider.insertBefore(cloneLast, realSlides[0])
        }
        this.disableCloneFocus(cloneLast)
        cloneLast.removeAttribute('data-carousel-server-clone')
        cloneLast.removeAttribute('inert')

        // Append clones in order so final DOM is [..., realN, firstClone, ...extraTrailing]
        const cloneFirst = realSlides[0].cloneNode(true) as HTMLElement
        this.eagerLoadCloneImages(cloneFirst)
        this.disableCloneFocus(cloneFirst)
        this.slider.appendChild(cloneFirst)

        for (let k = 1; k <= this.clonesPerSide - 1; k++) {
            const clone = realSlides[k % this.totalRealPages].cloneNode(
                true
            ) as HTMLElement
            this.eagerLoadCloneImages(clone)
            this.disableCloneFocus(clone)
            this.slider.appendChild(clone)
        }

        // Recalculate after adding clones
        this.getSlidesInfo()

        // Instantly position to first real slide
        this.scrollInstant(this.getSlideScrollLeft(this.clonesPerSide))
        this.pageIndex = 0
        this.slider.setAttribute('data-carousel-ready', 'true')
        this.updatePageTotalUI()
        this.updatePageNumberUI()
    }

    private get clonesPerSide(): number {
        return this.peek ? PEEK_CLONES_PER_SIDE : 1
    }

    /**
     * Remove clone slides from the tab order so keyboard users skip them entirely.
     * Clones exist only for the loop animation; real slides are the canonical content.
     */
    private disableCloneFocus(clone: HTMLElement): void {
        clone
            .querySelectorAll<HTMLElement>(
                'a[href], button, input, select, textarea, [tabindex]'
            )
            .forEach((el) => {
                el.tabIndex = -1
            })
    }

    /**
     * Eagerly loads images inside a cloned slide element so they are ready
     * before the user scrolls into them. The template uses a JS lazy-loader:
     * images have `class="lazyload"`, a blurred low-quality `src`, and the
     * full-quality URL in `data-src`/`data-srcset`. Off-screen clones are
     * never observed by the IntersectionObserver, so we swap the attributes
     * manually to prevent a blurred/white flash when the clone scrolls into view.
     */
    private eagerLoadCloneImages(clone: HTMLElement): void {
        clone
            .querySelectorAll<HTMLSourceElement>('source[data-srcset]')
            .forEach((source) => {
                if (source.dataset.srcset) {
                    source.srcset = source.dataset.srcset
                }
            })
        clone
            .querySelectorAll<HTMLImageElement>('img.lazyload')
            .forEach((img) => {
                if (img.dataset.src) {
                    img.src = img.dataset.src
                }
                if (img.dataset.srcset) {
                    img.srcset = img.dataset.srcset
                }
                img.classList.remove('lazyload')
                img.removeAttribute('loading')
            })
    }

    /**
     * Instantly moves the scroll position to the given left value.
     * Uses behavior:'instant' so the browser snaps atomically — with
     * scroll-snap-type active the snap adjustment is also instant, so
     * no smooth correction animation fires afterward.
     */
    private scrollInstant(left: number): void {
        if (typeof this.slider.scroll === 'function') {
            this.slider.scroll({
                left: Math.round(left),
                behavior: 'instant' as ScrollBehavior
            })
        } else {
            this.slider.scrollLeft = Math.round(left)
        }
    }

    /**
     * Calls callback once the current smooth scroll finishes.
     * Uses the scrollend event with a setTimeout fallback.
     */
    private afterSmoothScroll(callback: () => void): void {
        let called = false
        const done = (): void => {
            if (called) {
                return
            }
            called = true
            callback()
        }
        this.slider.addEventListener(EventType.SCROLL_END, done, { once: true })
        setTimeout(done, CarouselComponent.SMOOTH_SCROLL_TIMEOUT)
    }

    /**
     * Compute the exact scroll position to bring the slide at domIndex into the
     * snap-aligned position, accounting for scroll-snap-align: center when set.
     * Avoids a post-teleport CSS snap correction that can appear as a quick scroll.
     */
    public getSlideScrollLeft(domIndex: number): number {
        const slide = this.slides[domIndex]
        if (!slide || !this.slider) {
            return this.indexScrollingDistance(domIndex)
        }
        // Use getBoundingClientRect so the result is correct regardless of offsetParent
        const sliderRect = this.slider.getBoundingClientRect()
        const slideRect = slide.getBoundingClientRect()
        const slideContentLeft =
            this.slider.scrollLeft + (slideRect.left - sliderRect.left)
        // If the slide is narrower than the container it uses center snap alignment
        const centerOffset =
            slide.offsetWidth < this.slider.clientWidth
                ? (this.slider.clientWidth - slide.offsetWidth) / 2
                : 0
        return Math.max(
            0,
            Math.min(
                slideContentLeft - centerOffset,
                this.slider.scrollWidth - this.slider.clientWidth
            )
        )
    }

    /**
     * BUILD Pagination HTML List Items w/ Action Button and add to pagination <ol>
     * <li [classes]><button type=button [classes & attr]>a11y text</button></li>
     */
    public buildPaginationNavigationItems(): void {
        if (!this.paginationNavigation) {
            return
        }
        let navItems = ''
        // account for 0 index
        for (let i = 1; i <= this.totalPages; i++) {
            const currentClass =
                this.pageIndex === i - 1
                    ? carouselClass.paginationListItemActive
                    : ''

            navItems += `<li class="${carouselClass.paginationListItem}">
                <button type="button" class="${
                    carouselClass.paginationListItemButton
                } ${currentClass}"
                    ${carouselAttr.paginationNavToIndex}="${i - 1}">
                    <span class="${
                        CLASS_NAME.common.visuallyHidden
                    }">scroll to page ${i}</span>
                </button></li>`
        }
        // add to the pagination <ol>
        this.paginationNavigation.innerHTML = navItems
    }
}
